import { appEnvironment, configEnvironment } from '@optools/services/config/appConfig';
import { APP_ENVIRONMENTS, CONFIG_ENVIRONMENTS } from '@optools/services/config/types';
import { AuditSourceSystemMetadata } from '@optools/services/operator-gateway/types/generated/graphql';
import { formatMilliseconds } from '@optools/utils/datetime';
import { subHours } from 'date-fns';

export const formatDataDogTraceLinkUrl = (traceId: string, logStartTime: string) => {
  let currentAppEnvironment = appEnvironment;
  if (currentAppEnvironment === APP_ENVIRONMENTS.DEV) {
    currentAppEnvironment =
      configEnvironment === CONFIG_ENVIRONMENTS.QA ? APP_ENVIRONMENTS.QA : APP_ENVIRONMENTS.NPD;
  }

  const url = new URL(
    `https://us5.datadoghq.com/logs?query=env:${currentAppEnvironment} @mlb-trace-context.traceId:${traceId}`,
  );
  url.searchParams.append('cols', 'service');
  url.searchParams.append('messageDisplay', 'inline');
  url.searchParams.append('refresh_mode', 'sliding');
  url.searchParams.append('storage', 'hot');
  url.searchParams.append('stream_sort', 'time,asc');
  url.searchParams.append('viz', 'stream');
  url.searchParams.append('from_ts', formatMilliseconds(subHours(logStartTime, 1)));
  return url.toString();
};

/**
 * Filters operations by the currently selected source systems.
 *
 * Creates a record mapping source system names to their available operations,
 * but only includes source systems that are currently selected (or all if none selected).
 *
 * @param selectedSourceSystems - Array of currently selected source system names, or undefined if none selected
 * @param auditSourceSystems - Array of all available source system metadata containing operations
 * @returns A record where keys are source system names and values are arrays of operation options with label and value properties
 *
 */
export const filterOperationsBySourceSystems = (
  selectedSourceSystems: string[] | undefined,
  auditSourceSystems: AuditSourceSystemMetadata[],
) => {
  return auditSourceSystems.reduce<Record<string, { label: string; value: string }[]>>(
    (acc, sourceSystem) => {
      if (!selectedSourceSystems?.length || selectedSourceSystems?.includes(sourceSystem.name)) {
        const options = sourceSystem.operations.map((option) => ({
          label: option,
          value: option,
        }));
        acc[sourceSystem.name] = options;
      }
      return acc;
    },
    {},
  );
};
